import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "dialog", "title", "venue", "frames", "frame", "screening", "loading", "footer", "updatedAt", "updatedAtSource",
    "ticketAction", "ticketLink", "ticketLinkTemplate", "ticketLabel", "ticketUnavailable", "navigation", "previous", "next"
  ]

  screeningTargetConnected(link) {
    this.frameFor(link)
  }

  updatedAtSourceTargetConnected(source) {
    const frame = source.closest("[data-seat-map-modal-target='frame']")
    if (frame && !frame.hidden) this.updateUpdatedAt(frame)
  }

  beforeFrameRender(event) {
    const frame = event.currentTarget
    if (!this.dialogTarget.open || frame.hidden || this.dialogTarget.dataset.seatMapModalMode !== "tickets") return

    this.frameResize = { frame, height: this.dialogTarget.offsetHeight }
  }

  open(event) {
    if (event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return

    const { currentTarget } = event
    const frame = this.frameFor(currentTarget)
    if (!frame || this.frameLoaded(frame)) event.preventDefault()
    if (!this.dialogTarget.open) this.invoker = currentTarget
    this.activate(currentTarget)

    if (!this.dialogTarget.open) this.dialogTarget.showModal()
    document.documentElement.classList.add("overflow-hidden")
  }

  follow(event) {
    if (event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return

    const offset = event.currentTarget === this.previousTarget ? -1 : 1
    const link = this.navigationLinks?.[this.navigationIndex + offset]
    if (!link) {
      event.preventDefault()
      return
    }

    if (this.frameLoaded(this.frameFor(link))) {
      event.preventDefault()
      this.activate(link)
    } else {
      this.activate(link, false)
      // Let Turbo resolve the clicked arrow's current frame before repointing it.
      setTimeout(() => this.updateNavigation(link))
    }
  }

  navigate(event) {
    if (!this.dialogTarget.open || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
    if (event.target.closest?.("input, textarea, select, [contenteditable], [data-seat-map-target='scroller']")) return

    const control = event.key === "ArrowLeft" ? this.previousTarget :
      event.key === "ArrowRight" ? this.nextTarget : null
    if (!control?.hasAttribute("href")) return

    event.preventDefault()
    control.click()
  }

  activate(link, updateNavigation = true) {
    const { seatMapModalVenueParam, seatMapModalTicketUrlParam, seatMapModalTicketLabelParam,
      seatMapModalTicketUnavailableParam } = link.dataset
    const frame = this.frameFor(link)
    const seatMap = Boolean(link.dataset.seatMapModalScreening)

    this.dialogTarget.dataset.seatMapModalMode = seatMap ? "map" : "tickets"
    this.titleTarget.textContent = seatMap ? "Harta sălii" : "Bilete"
    this.venueTarget.textContent = seatMapModalVenueParam
    this.frameTargets.forEach((candidate) => { candidate.hidden = candidate !== frame })
    this.updateUpdatedAt(frame)
    if (frame && !this.frameLoaded(frame) && frame.dataset.seatMapModalLoading !== "true") {
      frame.dataset.seatMapModalLoading = "true"
      frame.replaceChildren(this.loadingTarget.content.cloneNode(true))
    }
    this.updateTicketLink(
      seatMapModalTicketUrlParam,
      seatMapModalTicketLabelParam,
      seatMapModalTicketUnavailableParam
    )
    if (updateNavigation) this.updateNavigation(link)
    this.redrawCharts(frame)
  }

  loaded(event) {
    const frame = event.currentTarget

    frame.dataset.seatMapModalLoaded = "true"
    delete frame.dataset.seatMapModalLoading
    this.screeningTargets
      .filter((candidate) => candidate.dataset.turboFrame === frame.id)
      .forEach((link) => { link.dataset.turboPrefetch = "false" })
    if (!frame.hidden) {
      this.updateUpdatedAt(frame)
      this.animateFrameResize(frame)
      this.redrawCharts(frame)
    }
    if (this.navigationLinks) this.updateNavigation(this.navigationLinks[this.navigationIndex])
  }

  updateNavigation(link) {
    const boundary = link.closest("[data-seat-map-modal-navigation]")
    const links = boundary ? [...boundary.querySelectorAll("[data-seat-map-modal-screening]")] : [link]
    const index = links.indexOf(link)

    this.navigationLinks = index === -1 ? [link] : links
    this.navigationIndex = index === -1 ? 0 : index
    this.navigationTarget.hidden = this.navigationLinks.length < 2
    this.updateNavigationLink(this.previousTarget, this.navigationLinks[this.navigationIndex - 1])
    this.updateNavigationLink(this.nextTarget, this.navigationLinks[this.navigationIndex + 1])
  }

  updateNavigationLink(control, link) {
    if (link) {
      control.href = link.href
      control.dataset.turboFrame = link.dataset.turboFrame
      control.setAttribute("aria-controls", link.dataset.turboFrame)
      if (this.frameLoaded(this.frameFor(link))) {
        control.dataset.turboPrefetch = "false"
      } else {
        delete control.dataset.turboPrefetch
      }
      control.removeAttribute("aria-disabled")
      control.removeAttribute("tabindex")
    } else {
      control.removeAttribute("href")
      control.removeAttribute("aria-controls")
      delete control.dataset.turboFrame
      delete control.dataset.turboPrefetch
      control.setAttribute("aria-disabled", "true")
      control.setAttribute("tabindex", "-1")
    }
  }

  frameFor(link) {
    const id = link.dataset.turboFrame
    if (!id) return null

    const existingFrame = document.getElementById(id)
    if (existingFrame && this.framesTarget.contains(existingFrame)) return existingFrame

    const frame = document.createElement("turbo-frame")
    frame.id = id
    frame.className = "block"
    frame.hidden = true
    frame.dataset.controller = "seat-map"
    frame.dataset.seatMapModalTarget = "frame"
    frame.dataset.action = [
      "turbo:before-frame-render->seat-map-modal#beforeFrameRender",
      "turbo:frame-load->seat-map-modal#loaded",
      "turbo:frame-load->seat-map#center",
      "resize@window->seat-map#fitHeight"
    ].join(" ")
    this.framesTarget.append(frame)

    return frame
  }

  frameLoaded(frame) {
    return frame?.dataset.seatMapModalLoaded === "true"
  }

  redrawCharts(frame) {
    if (!frame) return

    requestAnimationFrame(() => {
      window.Chartkick?.eachChart((chart) => {
        if (frame.contains(chart.getElement())) chart.redraw()
      })
    })
  }

  animateFrameResize(frame) {
    const resize = this.frameResize
    this.frameResize = null
    if (!resize || resize.frame !== frame || !this.dialogTarget.open ||
        window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    const loadedHeight = this.dialogTarget.offsetHeight
    if (Math.abs(resize.height - loadedHeight) < 1) return

    this.resizeAnimation?.cancel()
    const animation = this.dialogTarget.animate(
      { height: [`${resize.height}px`, `${loadedHeight}px`] },
      { duration: 260, easing: "cubic-bezier(0.23, 1, 0.32, 1)" }
    )
    this.resizeAnimation = animation
    const clearAnimation = () => {
      if (this.resizeAnimation === animation) this.resizeAnimation = null
    }
    animation.addEventListener("finish", clearAnimation, { once: true })
    animation.addEventListener("cancel", clearAnimation, { once: true })
  }

  close() {
    if (this.dialogTarget.open) this.dialogTarget.close()
  }

  closeOnBackdrop(event) {
    if (event.target === this.dialogTarget) this.close()
  }

  closed() {
    document.documentElement.classList.remove("overflow-hidden")
    this.resetFrameResize()
    this.invoker?.focus()
  }

  disconnect() {
    document.documentElement.classList.remove("overflow-hidden")
    this.resetFrameResize()
  }

  resetFrameResize() {
    this.frameResize = null
    this.resizeAnimation?.cancel()
    this.resizeAnimation = null
  }

  updateTicketLink(url, label, unavailableLabel) {
    this.hasTicketFooterContent = Boolean(url || unavailableLabel)
    this.ticketActionTarget.hidden = !url
    this.ticketUnavailableTarget.hidden = !unavailableLabel

    if (url) {
      if (!this.hasTicketLinkTarget) {
        this.ticketActionTarget.append(this.ticketLinkTemplateTarget.content.cloneNode(true))
      }
      this.ticketLinkTarget.href = url
      this.ticketLabelTarget.textContent = label
      this.ticketUnavailableTarget.textContent = ""
    } else {
      this.ticketUnavailableTarget.textContent = unavailableLabel || ""
    }

    this.updateFooterVisibility()
  }

  updateUpdatedAt(frame) {
    const label = frame?.querySelector("[data-seat-map-updated-label]")?.dataset.seatMapUpdatedLabel || ""

    this.updatedAtTarget.textContent = label
    this.updatedAtTarget.hidden = !label
    this.updateFooterVisibility()
  }

  updateFooterVisibility() {
    this.footerTarget.hidden = !this.hasTicketFooterContent && this.updatedAtTarget.hidden
  }
}
